import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { ChatState, ChatContextType, ChatMessage } from '../../types/chat';
import netlifyChatService from '../../services/netlifyChatService';
import { useLanguage } from '../../context/LanguageContext';
import { keepHubSpotChatHidden, openHubSpotHumanChat } from '../../utils/hubspotChatControl';

// Track IPs that have already been challenged
const challengedIPs = new Set<string>();

// Get client IP (simplified - in production you'd get this from the server)
const getClientIP = (): string => {
  // For now, use a combination of user agent and timestamp as a simple identifier
  // In production, this should be replaced with actual IP detection
  return `${navigator.userAgent}-${Math.floor(Date.now() / (1000 * 60 * 60))}`; // Changes every hour
};

// Initial state
const initialState: ChatState = {
  messages: [],
  isOpen: false,
  isLoading: false,
  error: null,
  hubSpotRedirect: null,
  challenge: null,
  pendingMessage: null,
  leadForm: null,
};

// Action types
type ChatAction =
  | { type: 'SEND_MESSAGE'; payload: string }
  | { type: 'RECEIVE_MESSAGE'; payload: string }
  | { type: 'START_STREAMING_MESSAGE' }
  | { type: 'APPEND_STREAM_TOKEN'; payload: string }
  | { type: 'REPLACE_STREAM_MESSAGE'; payload: string }
  | { type: 'FINISH_STREAMING_MESSAGE' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'TOGGLE_CHAT' }
  | { type: 'CLEAR_CHAT' }
  | { type: 'SET_IS_OPEN'; payload: boolean }
  | { type: 'SET_HUBSPOT_REDIRECT'; payload: { message: string; retryAfter?: number } }
  | { type: 'SET_CHALLENGE'; payload: { question: string; answer: string } }
  | { type: 'CLEAR_CHALLENGE' }
  | { type: 'SET_PENDING_MESSAGE'; payload: string }
  | { type: 'CLEAR_PENDING_MESSAGE' }
  | { type: 'MARK_IP_CHALLENGED'; payload: string }
  | { type: 'SHOW_LEAD_FORM'; payload: { reason?: string; requestSummary?: string; language?: string } }
  | { type: 'HIDE_LEAD_FORM' };

// Comprehensive challenge questions for client-side validation
// Using a subset of the full challenge system for client-side validation
const CLIENT_CHALLENGE_QUESTIONS = [
  // Math questions
  { question: "What is 2 + 3?", answer: "5" },
  { question: "What is 7 + 8?", answer: "15" },
  { question: "What is 12 - 5?", answer: "7" },
  { question: "What is 4 x 6?", answer: "24" },
  { question: "What is 15 ÷ 3?", answer: "5" },
  
  // Animal questions
  { question: "What animal says meow?", answer: "cat" },
  { question: "What animal says woof?", answer: "dog" },
  { question: "What animal says moo?", answer: "cow" },
  { question: "What animal says quack?", answer: "duck" },
  { question: "What animal says baa?", answer: "sheep" },
  
  // Color questions
  { question: "What color is the sky on a clear day?", answer: "blue" },
  { question: "What color is grass?", answer: "green" },
  { question: "What color is the sun?", answer: "yellow" },
  { question: "What color is snow?", answer: "white" },
  { question: "What color is a stop sign?", answer: "red" },
  
  // Logic questions
  { question: "If you have 3 apples and eat 1, how many do you have left?", answer: "2" },
  { question: "If you have 5 cookies and give 2 away, how many do you have?", answer: "3" },
  { question: "If you have 4 books and buy 3 more, how many do you have?", answer: "7" },
  { question: "If you have 10 dollars and spend 4, how much do you have left?", answer: "6" },
  { question: "If you have 6 pencils and lose 2, how many do you have?", answer: "4" },
  
  // Object questions
  { question: "What do you use to write on paper?", answer: "pen" },
  { question: "What do you use to cut paper?", answer: "scissors" },
  { question: "What do you use to tell time?", answer: "clock" },
  { question: "What do you use to open a door?", answer: "key" },
  { question: "What do you use to clean your teeth?", answer: "toothbrush" }
];

// Generate a random challenge
const generateChallenge = () => {
  const randomIndex = Math.floor(Math.random() * CLIENT_CHALLENGE_QUESTIONS.length);
  return CLIENT_CHALLENGE_QUESTIONS[randomIndex];
};

// Validate challenge answer locally
const normalizeChallengeAnswer = (answer: string) =>
  answer
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '');

const CHALLENGE_ANSWER_ALIASES: Record<string, string[]> = {
  cat: ['cat', 'kitty', 'kitten'],
  dog: ['dog', 'puppy'],
  cow: ['cow', 'cattle', 'bull', 'ox'],
  duck: ['duck'],
  sheep: ['sheep', 'goat', 'lamb'],
  blue: ['blue'],
  green: ['green'],
  yellow: ['yellow', 'orange'],
  white: ['white'],
  red: ['red'],
  pen: ['pen', 'pencil'],
  scissors: ['scissors', 'scissor'],
  clock: ['clock', 'watch'],
  key: ['key'],
  toothbrush: ['toothbrush', 'brush'],
};

const validateChallengeAnswer = (userAnswer: string, correctAnswer: string): boolean => {
  const normalizedUserAnswer = normalizeChallengeAnswer(userAnswer);
  const normalizedCorrectAnswer = normalizeChallengeAnswer(correctAnswer);
  const acceptedAnswers = CHALLENGE_ANSWER_ALIASES[normalizedCorrectAnswer] || [normalizedCorrectAnswer];

  return acceptedAnswers.some((answer) => normalizeChallengeAnswer(answer) === normalizedUserAnswer);
};

// Reducer function
const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case 'SEND_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: Date.now().toString(),
            content: action.payload,
            role: 'user',
            timestamp: new Date(),
          },
        ],
        isLoading: true,
        error: null,
      };

    case 'RECEIVE_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: (Date.now() + 1).toString(),
            content: action.payload,
            role: 'assistant',
            timestamp: new Date(),
          },
        ],
        isLoading: false,
        error: null,
      };

    case 'START_STREAMING_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id: `stream-${Date.now()}`,
            content: '',
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
          },
        ],
        isLoading: true,
        error: null,
      };

    case 'APPEND_STREAM_TOKEN': {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last?.role === 'assistant' && last.isStreaming) {
        messages[messages.length - 1] = {
          ...last,
          content: last.content + action.payload,
        };
      }
      return { ...state, messages, isLoading: false };
    }

    case 'REPLACE_STREAM_MESSAGE': {
      const messages = [...state.messages];
      const last = messages[messages.length - 1];
      if (last?.role === 'assistant' && last.isStreaming) {
        messages[messages.length - 1] = {
          ...last,
          content: action.payload,
        };
      }
      return { ...state, messages, isLoading: false };
    }

    case 'FINISH_STREAMING_MESSAGE': {
      const messages = state.messages.map((msg) =>
        msg.isStreaming ? { ...msg, isStreaming: false } : msg,
      );
      return {
        ...state,
        messages,
        isLoading: false,
        error: null,
      };
    }

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };

    case 'TOGGLE_CHAT':
      return {
        ...state,
        isOpen: !state.isOpen,
      };

    case 'SET_IS_OPEN':
      return {
        ...state,
        isOpen: action.payload,
      };

    case 'CLEAR_CHAT':
      return {
        ...state,
        messages: [],
        error: null,
        hubSpotRedirect: null,
        challenge: null,
        pendingMessage: null,
        leadForm: null,
      };

    case 'SET_HUBSPOT_REDIRECT':
      return {
        ...state,
        hubSpotRedirect: action.payload,
      };

    case 'SET_CHALLENGE':
      return {
        ...state,
        challenge: action.payload,
      };

    case 'CLEAR_CHALLENGE':
      return {
        ...state,
        challenge: null,
      };

    case 'SET_PENDING_MESSAGE':
      return {
        ...state,
        pendingMessage: action.payload,
      };

    case 'CLEAR_PENDING_MESSAGE':
      return {
        ...state,
        pendingMessage: null,
      };

    case 'MARK_IP_CHALLENGED':
      // Mark this IP as challenged (stored in the Set, not in state)
      challengedIPs.add(action.payload);
      return state;

    case 'SHOW_LEAD_FORM':
      return {
        ...state,
        leadForm: {
          visible: true,
          reason: action.payload.reason,
          requestSummary: action.payload.requestSummary,
          language: action.payload.language,
        },
      };

    case 'HIDE_LEAD_FORM':
      return {
        ...state,
        leadForm: null,
      };

    default:
      return state;
  }
};

// Create context
const ChatContext = createContext<ChatContextType | undefined>(undefined);

/** Pull recent user chat messages into a HubSpot form description. */
function extractRequestSummary(messages: ChatMessage[], extraUserMessage?: string): string {
  const fromHistory = messages
    .filter((m) => m.role === 'user' && !m.isStreaming && String(m.content || '').trim())
    .map((m) => String(m.content).trim());

  const parts: string[] = [];
  for (const text of fromHistory) {
    if (!parts.length || parts[parts.length - 1] !== text) {
      parts.push(text);
    }
  }

  if (extraUserMessage && extraUserMessage.trim()) {
    const trimmed = extraUserMessage.trim();
    if (!parts.length || parts[parts.length - 1] !== trimmed) {
      parts.push(trimmed);
    }
  }

  return parts.slice(-3).join('\n\n').slice(0, 2000);
}

// Provider component
export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { language: siteLanguage } = useLanguage();

  useEffect(() => {
    keepHubSpotChatHidden();
  }, []);

  const consumeAssistantResponse = async (message: string) => {
    dispatch({ type: 'START_STREAMING_MESSAGE' });

    // Capture request text before/while streaming (user message already in state)
    const requestSummary = extractRequestSummary(state.messages, message);

    const response = await netlifyChatService.streamMessage(
      message,
      {
        onToken: (token) => {
          dispatch({ type: 'APPEND_STREAM_TOKEN', payload: token });
        },
        onReplace: (full) => {
          dispatch({ type: 'REPLACE_STREAM_MESSAGE', payload: full });
        },
        onLeadForm: ({ reason, language }) => {
          dispatch({
            type: 'SHOW_LEAD_FORM',
            payload: { reason, requestSummary, language: language || siteLanguage },
          });
        },
      },
      { preferredLanguage: siteLanguage },
    );

    if (response.redirectToHubSpot) {
      // Replace empty/partial stream bubble with redirect message
      dispatch({ type: 'REPLACE_STREAM_MESSAGE', payload: response.message });
      dispatch({ type: 'FINISH_STREAMING_MESSAGE' });
      dispatch({
        type: 'SET_HUBSPOT_REDIRECT',
        payload: {
          message: response.hubSpotMessage || response.message,
          retryAfter: response.retryAfter,
        },
      });
      // AI limit → hand off to HubSpot human chat
      openHubSpotHumanChat();
      return;
    }

    if (response.requiresVerification) {
      dispatch({ type: 'REPLACE_STREAM_MESSAGE', payload: response.message });
      dispatch({ type: 'FINISH_STREAMING_MESSAGE' });
      dispatch({
        type: 'SET_CHALLENGE',
        payload: {
          question: response.challenge || '',
          answer: response.challengeAnswer || '',
        },
      });
      return;
    }

    if (response.message) {
      dispatch({ type: 'REPLACE_STREAM_MESSAGE', payload: response.message });
    }
    if (response.showLeadForm) {
      dispatch({
        type: 'SHOW_LEAD_FORM',
        payload: {
          reason: response.leadFormReason,
          requestSummary,
          language: response.replyLanguage || siteLanguage,
        },
      });
    }
    dispatch({ type: 'FINISH_STREAMING_MESSAGE' });
    dispatch({ type: 'CLEAR_CHALLENGE' });
  };

  const sendMessage = async (message: string) => {
    try {
      // If there's an active challenge, validate the answer locally
      if (state.challenge) {
        const isValid = validateChallengeAnswer(message, state.challenge.answer);
        
        if (isValid) {
          // Clear the challenge
          dispatch({ type: 'CLEAR_CHALLENGE' });
          
          // Mark this IP as successfully challenged (in case it wasn't marked before)
          const clientIP = getClientIP();
          dispatch({ type: 'MARK_IP_CHALLENGED', payload: clientIP });
          
          // Send the original pending message to the server
          if (state.pendingMessage) {
            const pending = state.pendingMessage;
            dispatch({ type: 'CLEAR_PENDING_MESSAGE' });

            // Message was already added when the challenge started — do not SEND_MESSAGE again
            const alreadyInChat = state.messages.some(
              (m) => m.role === 'user' && String(m.content).trim() === pending.trim(),
            );
            if (!alreadyInChat) {
              dispatch({ type: 'SEND_MESSAGE', payload: pending });
            }

            await consumeAssistantResponse(pending);
          }
        } else {
          // Add error message for failed challenge
          dispatch({ 
            type: 'RECEIVE_MESSAGE', 
            payload: 'Please answer the verification question correctly.' 
          });
        }
        return;
      }

      // Get client IP for tracking
      const clientIP = getClientIP();
      
      // Check if this is the first message from this IP
      const isFirstMessageFromIP = !challengedIPs.has(clientIP);
      
      // Determine if we should trigger a challenge
      let shouldTriggerChallenge = false;
      
      if (isFirstMessageFromIP) {
        // Guarantee challenge for first message from new IP
        shouldTriggerChallenge = true;
        console.log('First message from new IP - triggering challenge');
      } else {
        // 20% chance for subsequent messages
        shouldTriggerChallenge = Math.random() < 0.2;
      }
      
      if (shouldTriggerChallenge) {
        const challenge = generateChallenge();
        
        // Store the original message
        dispatch({ type: 'SET_PENDING_MESSAGE', payload: message });
        
        // Add user message to chat
        dispatch({ type: 'SEND_MESSAGE', payload: message });
        
        // Set the challenge
        dispatch({ 
          type: 'SET_CHALLENGE', 
          payload: challenge 
        });
        
        // Mark this IP as challenged
        dispatch({ type: 'MARK_IP_CHALLENGED', payload: clientIP });
        
        // Add challenge message
        dispatch({ 
          type: 'RECEIVE_MESSAGE', 
          payload: `Please verify you are human by answering this question: ${challenge.question}` 
        });
        return;
      }

      // Normal message processing
      dispatch({ type: 'SEND_MESSAGE', payload: message });
      await consumeAssistantResponse(message);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    }
  };

  const toggleChat = () => {
    dispatch({ type: 'TOGGLE_CHAT' });
  };

  const clearChat = () => {
    dispatch({ type: 'CLEAR_CHAT' });
    netlifyChatService.clearHistory();
  };

  const openHumanChat = () => {
    const opened = openHubSpotHumanChat();
    const payload = opened
      ? 'Opening live chat with our team now. Look for the HubSpot chat on the left — you can keep using this AI agent anytime.'
      : 'Opening live chat with our team. If HubSpot chat does not appear in a few seconds, refresh the page and try Human again, or email us from the Contact page.';
    dispatch({
      type: 'RECEIVE_MESSAGE',
      payload,
    });
    netlifyChatService.recordAssistantNote(payload);
  };

  const dismissLeadForm = () => {
    dispatch({ type: 'HIDE_LEAD_FORM' });
  };

  const submitLeadForm = async (data: {
    firstName: string;
    lastName: string;
    email: string;
    company?: string;
    notes?: string;
  }) => {
    try {
      const pageUri =
        typeof window !== 'undefined'
          ? `${window.location.origin}${window.location.pathname}`
          : 'https://www.alioit.com/';
      const requestSummary =
        state.leadForm?.requestSummary ||
        extractRequestSummary(state.messages);
      const description = [requestSummary, data.notes].filter(Boolean).join('\n\n');

      const res = await fetch('/.netlify/functions/hubspot-chat-lead', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          company: data.company,
          interest: state.leadForm?.reason || 'Website AI Chat',
          description: description || 'Submitted via website AI chat',
          notes: description,
          pageUri,
          pageName: 'AI Chat',
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || 'Could not save your contact details.');
      }
      dispatch({ type: 'HIDE_LEAD_FORM' });
      dispatch({
        type: 'RECEIVE_MESSAGE',
        payload:
          'Thanks — your contact details were sent to our team. We will follow up soon. You can also book a call at https://calendly.com/alioit/group-meet',
      });
      netlifyChatService.recordAssistantNote(
        'Thanks — your contact details were sent to our team. We will follow up soon. You can also book a call at https://calendly.com/alioit/group-meet',
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit contact form.';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    }
  };

  const value: ChatContextType = {
    state,
    sendMessage,
    toggleChat,
    clearChat,
    openHumanChat,
    submitLeadForm,
    dismissLeadForm,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

// Custom hook to use the chat context
export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
